export const BASE_URL = "https://www.inanederdal.se";
export const SITE_NAME = "Ina Nederdal";
export const DEFAULT_LANGUAGE = "sv";
export const DEFAULT_OG_LOCALE = "sv_SE";
export const DEFAULT_SOCIAL_IMAGE =
  "https://www.inanederdal.se/images/social.jpg";

const GENERIC_SHELL_TITLE = SITE_NAME;
const GENERIC_SHELL_DESCRIPTION = "Porträtt- och lifestyle-fotograf i Stockholm.";

const STATIC_ROUTE_SEO = {
  home: {
    path: "/",
    title: "Ina Nederdal - Fotograf Stockholm",
    description:
      "Ina Nederdal – porträtt- och lifestyle-fotograf i Stockholm. Mammaporträtt, gravidfoto och bröllop. Boka din fotografering idag.",
  },
  work: {
    path: "/work",
    title: "Portfolio - Ina Nederdal",
    description:
      "Se Ina Nederdals portfolio med porträtt-, lifestyle- och redaktionell fotografering från Stockholm.",
  },
  about: {
    path: "/about",
    title: "About - Ina Nederdal",
    description:
      "Ina Nederdal – frilans porträtt- och lifestyle-fotograf i Stockholm. Specialiserad på lifestyle, inredning och mamma-, gravid- och familjefotografering.",
  },
  hejMamma: {
    path: "/hej-mamma",
    title: "Gravidfoto och mammaporträtt i Stockholm | Ina Nederdal",
    description:
      "Mammaporträtt i Stockholm – för dig som väntar barn, har en bebis, eller vill ha bilder med dina barn. Gravidfoto, mamma & barn, eller hela familjen.",
  },
  wedding: {
    path: "/brollopsfotograf",
    title: "Bröllopsfotograf i Stockholm | Ina Nederdal",
    description:
      "Bröllopsfotograf i dokumentär stil. Jag finns med hela dagen eller några timmar – porträtt, vigsel, tårar, kramar och dans.",
  },
  booking: {
    path: "/booking",
    title: "Boka fotografering - Ina Nederdal",
    description:
      "Boka en fotografering med Ina Nederdal i Stockholm. Porträtt, lifestyle, gravidfoto, familj och bröllop.",
  },
  notFound: {
    path: "/404",
    title: "Sidan hittades inte - Ina Nederdal",
    description:
      "Sidan du letar efter finns inte. Gå tillbaka till startsidan eller utforska Ina Nederdals portfolio.",
    noindex: true,
  },
};

export const STATIC_ROUTE_IDS = Object.freeze(Object.keys(STATIC_ROUTE_SEO));

const normalizeWhitespace = (value) => (value || "").replace(/\s+/g, " ").trim();

const trimTrailingSlash = (value) => (value.length > 1 ? value.replace(/\/+$/, "") : value);

const joinPathSegments = (...segments) => {
  const normalizedSegments = segments
    .map((segment) => normalizeWhitespace(segment).replace(/^\/+|\/+$/g, ""))
    .filter(Boolean);

  return normalizedSegments.length === 0 ? "/" : `/${normalizedSegments.join("/")}`;
};

export const normalizePath = (inputPath = "/") => {
  if (!inputPath || inputPath === "/") {
    return "/";
  }

  const withLeadingSlash = inputPath.startsWith("/") ? inputPath : `/${inputPath}`;
  return trimTrailingSlash(withLeadingSlash);
};

export const getCanonicalUrl = (inputPath = "/") => {
  const normalizedPath = normalizePath(inputPath);
  return normalizedPath === "/" ? BASE_URL : `${BASE_URL}${normalizedPath}`;
};

export const slugToLabel = (slug = "") =>
  normalizeWhitespace(
    slug
      .split("-")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" "),
  );

export const buildSeoMetadata = ({
  path,
  title,
  description,
  lang = DEFAULT_LANGUAGE,
  image = DEFAULT_SOCIAL_IMAGE,
  noindex = false,
} = {}) => {
  const normalizedPath = normalizePath(path);
  const canonicalUrl = getCanonicalUrl(normalizedPath);
  const finalTitle = normalizeWhitespace(title) || GENERIC_SHELL_TITLE;
  const finalDescription = normalizeWhitespace(description) || GENERIC_SHELL_DESCRIPTION;

  return {
    path: normalizedPath,
    title: finalTitle,
    description: finalDescription,
    lang,
    image: image || DEFAULT_SOCIAL_IMAGE,
    canonicalUrl,
    ogUrl: canonicalUrl,
    ogLocale: lang === "sv" ? DEFAULT_OG_LOCALE : "en_US",
    noindex,
    robots: noindex ? "noindex, nofollow" : undefined,
  };
};

export const getGenericShellMetadata = () => ({
  title: GENERIC_SHELL_TITLE,
  description: GENERIC_SHELL_DESCRIPTION,
  image: DEFAULT_SOCIAL_IMAGE,
});

export const getStaticRouteSeo = (routeId, overrides = {}) => {
  const baseRoute = STATIC_ROUTE_SEO[routeId];

  if (!baseRoute) {
    throw new Error(`Unknown static SEO route id: ${routeId}`);
  }

  return buildSeoMetadata({
    ...baseRoute,
    ...overrides,
    image: overrides.image || baseRoute.image || DEFAULT_SOCIAL_IMAGE,
    noindex: overrides.noindex ?? baseRoute.noindex ?? false,
  });
};

export const buildCategorySeo = ({
  categorySlug,
  categoryName,
  image,
  lang = DEFAULT_LANGUAGE,
  noindex = false,
} = {}) => {
  const resolvedSlug = normalizeWhitespace(categorySlug);
  const resolvedName = normalizeWhitespace(categoryName) || slugToLabel(resolvedSlug) || "Portfolio";

  return buildSeoMetadata({
    path: joinPathSegments("work", resolvedSlug),
    title: `${resolvedName} - ${SITE_NAME}`,
    description: `Se ${resolvedName}-fotografering av Ina Nederdal. Frilans porträtt- och lifestyle-fotograf i Stockholm.`,
    image,
    lang,
    noindex,
  });
};

export const buildJobSeo = ({
  categorySlug,
  jobSlug,
  jobTitle,
  description,
  image,
  lang = DEFAULT_LANGUAGE,
  noindex = false,
} = {}) => {
  const resolvedTitle = normalizeWhitespace(jobTitle) || slugToLabel(jobSlug) || SITE_NAME;
  const fallbackDescription = `${resolvedTitle} - fotografering av Ina Nederdal, frilans fotograf i Stockholm.`;

  return buildSeoMetadata({
    path: joinPathSegments("work", categorySlug, jobSlug),
    title: `${resolvedTitle} - ${SITE_NAME}`,
    description: normalizeWhitespace(description) || fallbackDescription,
    image,
    lang,
    noindex,
  });
};
